/**
 * NeoCore · Errores del sistema
 * ─────────────────────────────
 * Panel simple para monitorear errores capturados desde edge functions.
 * Lee `error_log` (poblado por _shared/errors.ts en el backend).
 *
 * Filtros:
 * - Solo no resueltos vs todos
 * - Por función (kyc-ocr-verify, inscripcion-digital, etc)
 * - Por categoría (ocr, external_api, validation, etc)
 *
 * Acciones por fila:
 * - Ver contexto completo (jsonb) → modal
 * - Marcar como resuelto → nota opcional
 * - Copiar short_id → para citar en soporte
 */

import { useEffect, useMemo, useState } from 'react'
import {
  CheckCircle,
  Copy,
  ArrowsClockwise,
  Funnel,
  X,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type ErrorRow = {
  id: string
  short_id: string
  code: string
  category: string
  source: string
  edge_function: string | null
  message: string
  user_message: string | null
  context: Record<string, unknown>
  actor_role: string | null
  request_id: string | null
  ip_address: string | null
  retry_suggested: boolean
  resolved_at: string | null
  resolved_note: string | null
  occurred_at: string
}

const CATEGORY_LABELS: Record<string, string> = {
  ocr: 'OCR',
  auth: 'Auth',
  payment: 'Pagos',
  external_api: 'API externa',
  validation: 'Validación',
  storage: 'Storage',
  db: 'Base de datos',
  notification: 'Notificaciones',
  unknown: 'Desconocido',
}

const CATEGORY_COLORS: Record<string, string> = {
  ocr: '#8B5CF6',
  auth: '#EF4444',
  payment: '#F59E0B',
  external_api: '#F97316',
  validation: '#3B82F6',
  storage: '#06B6D4',
  db: '#10B981',
  notification: '#EC4899',
  unknown: '#6B7280',
}

export function NeoCoreErroresPage() {
  const [rows, setRows] = useState<ErrorRow[]>([])
  const [loading, setLoading] = useState(true)
  const [filterUnresolved, setFilterUnresolved] = useState(true)
  const [filterFunction, setFilterFunction] = useState<string>('')
  const [filterCategory, setFilterCategory] = useState<string>('')
  const [selected, setSelected] = useState<ErrorRow | null>(null)

  async function load() {
    setLoading(true)
    let q = supabase
      .from('error_log')
      .select('*')
      .order('occurred_at', { ascending: false })
      .limit(200)
    if (filterUnresolved) q = q.is('resolved_at', null)
    if (filterFunction) q = q.eq('edge_function', filterFunction)
    if (filterCategory) q = q.eq('category', filterCategory)
    const { data, error } = await q
    if (error) {
      console.error('[NeoCoreErroresPage] load error:', error)
      setRows([])
    } else {
      setRows((data ?? []) as ErrorRow[])
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [filterUnresolved, filterFunction, filterCategory])

  const stats = useMemo(() => {
    const unresolved = rows.filter((r) => !r.resolved_at).length
    const byCategory = new Map<string, number>()
    const byCode = new Map<string, number>()
    for (const r of rows) {
      byCategory.set(r.category, (byCategory.get(r.category) ?? 0) + 1)
      byCode.set(r.code, (byCode.get(r.code) ?? 0) + 1)
    }
    return {
      total: rows.length,
      unresolved,
      byCategory: Array.from(byCategory.entries()).sort((a, b) => b[1] - a[1]),
      topCodes: Array.from(byCode.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5),
    }
  }, [rows])

  const availableFunctions = useMemo(() => {
    const set = new Set<string>()
    rows.forEach((r) => r.edge_function && set.add(r.edge_function))
    return Array.from(set).sort()
  }, [rows])

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Errores del sistema</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted-foreground)', fontSize: 13 }}>
            Ledger de errores capturados desde edge functions.
            Últimas 200 entradas.
          </p>
        </div>
        <button
          onClick={load}
          style={{
            padding: '8px 14px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--surface)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
          }}
        >
          <ArrowsClockwise size={14} /> Actualizar
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Total" value={stats.total} />
        <StatCard label="Sin resolver" value={stats.unresolved} accent={stats.unresolved > 0 ? '#EF4444' : undefined} />
        <StatCard label="Categorías afectadas" value={stats.byCategory.length} />
        <StatCard label="Código más frecuente" value={stats.topCodes[0]?.[0] ?? '—'} small />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Funnel size={16} color="var(--muted-foreground)" />
        <FilterChip
          active={filterUnresolved}
          onClick={() => setFilterUnresolved((v) => !v)}
          label={filterUnresolved ? 'Solo sin resolver' : 'Todos'}
        />
        <select
          value={filterFunction}
          onChange={(e) => setFilterFunction(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="">Todas las funciones</option>
          {availableFunctions.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          style={filterSelectStyle}
        >
          <option value="">Todas las categorías</option>
          {Object.entries(CATEGORY_LABELS).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
      ) : rows.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--muted-foreground)',
          border: '1px dashed var(--border)', borderRadius: 12,
        }}>
          <CheckCircle size={32} color="#10B981" weight="fill" />
          <p style={{ margin: '10px 0 0' }}>Sin errores registrados con estos filtros.</p>
          <p style={{ margin: '4px 0 0', fontSize: 12 }}>Todo funcionando.</p>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--n-50)', borderBottom: '1px solid var(--border)' }}>
              <tr>
                <th style={thStyle}>Cuándo</th>
                <th style={thStyle}>Short ID</th>
                <th style={thStyle}>Código</th>
                <th style={thStyle}>Categoría</th>
                <th style={thStyle}>Función</th>
                <th style={{ ...thStyle, width: '35%' }}>Mensaje</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: r.resolved_at ? 'var(--n-50)' : undefined,
                    opacity: r.resolved_at ? 0.6 : 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelected(r)}
                >
                  <td style={tdStyle}>{formatRelative(r.occurred_at)}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                    <span onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(r.short_id) }} title="Copiar">
                      {r.short_id} <Copy size={10} style={{ opacity: 0.5 }} />
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}>{r.code}</td>
                  <td style={tdStyle}>
                    <CategoryBadge category={r.category} />
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12 }}>{r.edge_function ?? '—'}</td>
                  <td style={{ ...tdStyle, fontSize: 12 }}>
                    <div style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.message}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {r.resolved_at ? (
                      <span style={{ color: '#10B981', fontSize: 11, fontWeight: 600 }}>✓ Resuelto</span>
                    ) : r.retry_suggested ? (
                      <span style={{ color: '#F59E0B', fontSize: 11 }}>Retry sugerido</span>
                    ) : (
                      <span style={{ color: '#EF4444', fontSize: 11 }}>Abierto</span>
                    )}
                  </td>
                  <td style={tdStyle}>→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal detalle */}
      {selected && (
        <DetalleModal
          row={selected}
          onClose={() => setSelected(null)}
          onResolved={() => { setSelected(null); load() }}
        />
      )}
    </div>
  )
}

function StatCard({ label, value, accent, small }: { label: string; value: string | number; accent?: string; small?: boolean }) {
  return (
    <div style={{
      padding: 14,
      border: '1px solid var(--border)',
      borderRadius: 12,
      background: 'var(--surface)',
    }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: small ? 15 : 22,
        fontWeight: 700,
        color: accent ?? 'var(--ink)',
        fontFamily: small ? 'monospace' : undefined,
      }}>{value}</div>
    </div>
  )
}

function FilterChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px',
        border: `1px solid ${active ? 'var(--brand-600)' : 'var(--border)'}`,
        background: active ? 'var(--brand-50, #fff5ed)' : 'var(--surface)',
        color: active ? 'var(--brand-600)' : 'var(--ink)',
        borderRadius: 999,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  )
}

function CategoryBadge({ category }: { category: string }) {
  const color = CATEGORY_COLORS[category] ?? '#6B7280'
  const label = CATEGORY_LABELS[category] ?? category
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 600,
      background: `${color}20`,
      color,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>
      {label}
    </span>
  )
}

function DetalleModal({ row, onClose, onResolved }: { row: ErrorRow; onClose: () => void; onResolved: () => void }) {
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  async function markResolved() {
    setSaving(true)
    const { error } = await supabase
      .from('error_log')
      .update({ resolved_at: new Date().toISOString(), resolved_note: note || null })
      .eq('id', row.id)
    setSaving(false)
    if (error) {
      alert('No se pudo marcar como resuelto: ' + error.message)
      return
    }
    onResolved()
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          maxWidth: 800,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: 24,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--muted-foreground)' }}>{row.short_id}</div>
            <h2 style={{ margin: '4px 0 0', fontSize: 18, fontFamily: 'monospace' }}>{row.code}</h2>
            <CategoryBadge category={row.category} />
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <MetaItem label="Cuándo" value={new Date(row.occurred_at).toLocaleString()} />
          <MetaItem label="Función" value={row.edge_function ?? '—'} />
          <MetaItem label="Origen" value={row.source} />
          <MetaItem label="Rol actor" value={row.actor_role ?? '—'} />
          <MetaItem label="IP" value={row.ip_address ?? '—'} />
          <MetaItem label="Retry sugerido" value={row.retry_suggested ? 'Sí' : 'No'} />
        </div>

        <Section title="Mensaje (dev)">
          <pre style={preStyle}>{row.message}</pre>
        </Section>

        {row.user_message && (
          <Section title="Mensaje al usuario">
            <div style={{ padding: 12, background: 'var(--n-50)', borderRadius: 8, fontSize: 13 }}>
              {row.user_message}
            </div>
          </Section>
        )}

        <Section title="Contexto completo">
          <pre style={preStyle}>{JSON.stringify(row.context, null, 2)}</pre>
        </Section>

        {row.resolved_at ? (
          <div style={{ padding: 12, background: '#10B98115', borderRadius: 8, marginTop: 16 }}>
            <strong style={{ color: '#10B981' }}>✓ Marcado como resuelto</strong>
            <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 4 }}>
              {new Date(row.resolved_at).toLocaleString()}
            </div>
            {row.resolved_note && (
              <div style={{ fontSize: 13, marginTop: 6, fontStyle: 'italic' }}>"{row.resolved_note}"</div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 20, padding: 12, border: '1px dashed var(--border)', borderRadius: 8 }}>
            <label style={{ fontSize: 12, color: 'var(--muted-foreground)', display: 'block', marginBottom: 6 }}>
              Nota de resolución (opcional)
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ej: reintenté y funcionó / rate limit temporal Anthropic"
              style={{
                width: '100%', padding: 8, border: '1px solid var(--border)',
                borderRadius: 6, fontSize: 13, marginBottom: 8, boxSizing: 'border-box',
              }}
            />
            <button
              onClick={markResolved}
              disabled={saving}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: 8,
                background: 'var(--brand-600)', color: '#fff', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
              }}
            >
              {saving ? 'Guardando…' : 'Marcar como resuelto'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--muted-foreground)', marginBottom: 6 }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, marginTop: 2 }}>{value}</div>
    </div>
  )
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `hace ${hr}h`
  const days = Math.floor(hr / 24)
  if (days < 7) return `hace ${days}d`
  return d.toLocaleDateString()
}

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--muted-foreground)',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  verticalAlign: 'middle',
}

const preStyle: React.CSSProperties = {
  padding: 12,
  background: 'var(--n-50)',
  borderRadius: 8,
  fontSize: 12,
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 300,
  overflow: 'auto',
  margin: 0,
}

const filterSelectStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--surface)',
  fontSize: 12,
  cursor: 'pointer',
}
